import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';

import { auditLogApi, AuditLogsFilters, AuditLogsResponse } from '../../api/auditLog';
import { useUsuarios } from '../../hooks/useUsuarios';

const AuditLogViewer: React.FC = () => {
  const [filters, setFilters] = useState<AuditLogsFilters>({
    page: 1,
    limit: 50,
  });

  const { rows: usuarios } = useUsuarios();

  const [openRows, setOpenRows] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useQuery<AuditLogsResponse, Error>({
    queryKey: ['audit-logs', filters],
    queryFn: () => auditLogApi.getAll(filters),
    placeholderData: (previousData) => previousData,
  });

  const handleFilterChange = (key: keyof AuditLogsFilters, value: unknown) => {
    setFilters((prev: AuditLogsFilters) => ({
      ...prev,
      [key]: value || undefined,
      page: key !== 'page' ? 1 : (value as number), // reset page si no es cambio de página
    }));
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    handleFilterChange('page', newPage + 1); // MUI usa base 0, API usa base 1
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('limit', parseInt(event.target.value, 10));
    handleFilterChange('page', 1);
  };

  const toggleRow = (logId: number) => {
    setOpenRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getAccionColor = (accion: string) => {
    switch (accion) {
      case 'INSERT':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const usuariosById = useMemo(() => new Map(usuarios.map((u) => [u.id, u])), [usuarios]);

  const renderUsuario = (usuarioId: number | null) => {
    if (!usuarioId) return '—';
    const usuario = usuariosById.get(usuarioId);
    const label = usuario?.nombreCompleto || usuario?.email || 'Usuario';
    return `${label} (#${usuarioId})`;
  };

  const modulos = useMemo(() => {
    const rows = data?.data ?? [];
    return Array.from(new Set(rows.map((r) => r.tabla))).sort();
  }, [data]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Logs de Auditoría
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Vista de solo lectura del historial completo de operaciones del sistema
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Módulo</InputLabel>
              <Select
                value={filters.modulo || ''}
                label="Módulo"
                onChange={(e) => handleFilterChange('modulo', e.target.value)}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {modulos.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="ID Usuario"
              type="number"
              value={filters.actorId || ''}
              onChange={(e) =>
                handleFilterChange('actorId', e.target.value ? parseInt(e.target.value) : undefined)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Acción</InputLabel>
              <Select
                value={filters.accion || ''}
                label="Acción"
                onChange={(e) => handleFilterChange('accion', e.target.value)}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                <MenuItem value="INSERT">INSERT</MenuItem>
                <MenuItem value="UPDATE">UPDATE</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Fecha desde"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={filters.desde || ''}
              onChange={(e) => handleFilterChange('desde', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Fecha hasta"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={filters.hasta || ''}
              onChange={(e) => handleFilterChange('hasta', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de resultados */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los logs: {(error as Error)?.message || 'Error desconocido'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {(() => {
            const rows = data?.data ?? [];
            const meta = data?.meta;

            return (
              <>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={50} />
                        <TableCell>ID</TableCell>
                        <TableCell>Módulo</TableCell>
                        <TableCell>Acción</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Fecha/Hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No se encontraron registros
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((log) => (
                          <React.Fragment key={log.id}>
                            <TableRow hover>
                              <TableCell>
                                <IconButton size="small" onClick={() => toggleRow(log.id)}>
                                  {openRows.has(log.id) ? (
                                    <KeyboardArrowUpIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell>{log.id}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {log.tabla}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.accion}
                                  size="small"
                                  color={getAccionColor(log.accion)}
                                />
                              </TableCell>
                              <TableCell>{renderUsuario(log.usuarioId)}</TableCell>
                              <TableCell>
                                {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss', {
                                  locale: es,
                                })}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                <Collapse in={openRows.has(log.id)} timeout="auto" unmountOnExit>
                                  <Box sx={{ margin: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Datos JSON:
                                    </Typography>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 2,
                                        backgroundColor: 'grey.50',
                                        maxHeight: 400,
                                        overflow: 'auto',
                                      }}
                                    >
                                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                                        {JSON.stringify(log.datosJson, null, 2)}
                                      </pre>
                                    </Paper>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {meta && (
                  <TablePagination
                    component="div"
                    count={meta.total}
                    page={(meta.page || 1) - 1} // MUI usa base 0
                    onPageChange={handleChangePage}
                    rowsPerPage={meta.limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
                    }
                  />
                )}
              </>
            );
          })()}
        </>
      )}
    </Box>
  );
};

export default AuditLogViewer;
